import { AnyAction } from 'redux';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, ComplexValue } from 'app/types-shared';
import { STATE } from 'app/constants';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { favouriteAdminTranslation as text } from '../constants';
import { defaultMaxResults } from './constants';

import {
  RequestList, ReceiveList, FailList,
  RequestDeleteFavourite, ReceiveDeleteFavourite, FailDeleteFavourite,
  RequestChangeOwner, ReceiveChangeOwner, FailChangeOwner, ClearList, ClearChangeOwner,
  UpdateChangeOwnerIsDirty, ChangeOwnerFieldsByKey, ResetChangeOwnerValidation,
  UpdateChangeOwnerValidation, RequestChangeUserGroups, ReceiveChangeUserGroups, FailChangeUserGroups,
  UpdateChangeUserGroupsValidation, ClearChangeUserGroups, UpdateChangeUserGroupsIsDirty,
  ChangeUserGroupsFieldsByKey, ResetChangeUserGroupsValidation, UpdateChangeUserGroupsState,
  UpdateChangeUsersIsDirty, UpdateChangeUsersState, ChangeUsersFieldsByKey, ResetChangeUsersValidation,
  ClearChangeUsers, UpdateChangeUsersValidation, FailChangeUsers, ReceiveChangeUsers, RequestChangeUsers,
  UpdateOwnersValidation, ResetOwnersValidation
} from './action-types';
import { api } from './api';
import { mapList } from './map-list';
import { FavouriteSearchModel } from './types-store';
import { ChangeOwnerField } from 'app/area/favourite-administration/list/change-owner/types';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ChangeOptionType } from 'app/area/favourite-administration/list/types';
import { DisplayModal } from 'app/shell/action-types';
import { MODAL } from 'app/shell/modal-host-types';

const callChangeOwnerApi = (
  mode: ChangeOptionType,
  fromOwner: number,
  toOwners: number[],
  applyOnDescendants: boolean,
  requestedSearchModel: FavouriteSearchModel
) => {
  switch (mode) {
    case ChangeOptionType.Add:
      return api.addOwner({ owners: toOwners, applyOnDescendants, searchParameters: requestedSearchModel });
    default:
      return api.changeOwner({ owner: fromOwner, toOwners, applyOnDescendants, searchParameters: requestedSearchModel });
  }
};

const dispatchChangeOwnerValidationError = (dispatch: any, mode: ChangeOptionType, err: any) => {
  if (!err?.validationErrors) {
    return;
  }

  const validation = complexValidationFromServer(err.validationErrors);

  const isMultiSelectMode = mode === ChangeOptionType.Add;
  dispatch(isMultiSelectMode ? new UpdateOwnersValidation(validation) : new UpdateChangeOwnerValidation(validation));
};

export const actionCreators = {

  loadList: (
    model: FavouriteSearchModel,
    startIndex: number = 0,
    maxResults: number = defaultMaxResults
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const requestId = Date.now();
      dispatch(new RequestList(requestId, model, startIndex === 0));

      try {
        const listResponse = await api.loadList({ model, startIndex, maxResults });

        dispatch(new ReceiveList(
          requestId,
          mapList(listResponse.results),
          listResponse.totalNumberOfResults,
          listResponse.editableNumberOfResults)
        );
      } catch (err) {
        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.favouriteAdministration,
            translationId: text.no_access_to_favourite_admin
          });
        }

        dispatch(new FailList(requestId));
      }
    },

  deleteFavourite: (id: number, successMessage: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestDeleteFavourite(id));

      try {

        await api.deleteFavourite(id);
        dispatch(new ReceiveDeleteFavourite(id));
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailDeleteFavourite(id));
      }
    },

  changeOwner: (
    mode: ChangeOptionType,
    fromOwner: number,
    toOwners: number[],
    applyOnDescendants: boolean,
    numberOfResults: number,
    requestedSearchModel: FavouriteSearchModel,
    successMessage: string,
    onSuccess: () => void
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestChangeOwner());

      try {
        await callChangeOwnerApi(mode, fromOwner, toOwners, applyOnDescendants, requestedSearchModel);

        dispatch(new ReceiveChangeOwner());

        if (mode === ChangeOptionType.Add) {
          dispatch(new ResetOwnersValidation());
        }

        dispatch(new SuccessNotification(
          replaceTemplateString(
            successMessage,
            numberOfResults)
        ));

        onSuccess();
      } catch (err) {

        dispatch(new FailChangeOwner());

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatchChangeOwnerValidationError(dispatch, mode, err);
      }
    },

  resetOwnersValidation: (): ActionDispatching<any, any> => (dispatch: any) => {
    dispatch(new ResetOwnersValidation());
  },

  clearChangeOwner: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new ClearChangeOwner());
    },

  updateChangeOwnerIsDirty: (value: boolean): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateChangeOwnerIsDirty(value));
  },

  updateChangeOwnerFieldsByKey: <T>(key: ChangeOwnerField, value: T): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ChangeOwnerFieldsByKey(key, value));
  },

  resetChangeOwnerValidation: (key: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetChangeOwnerValidation(key));
  },

  changeUserGroups: (
    stateId: ChangeOptionType,
    replaceIds: ComplexValue<number>[],
    applyOnDescendants: boolean,
    numberOfResults: number,
    requestedSearchModel: FavouriteSearchModel,
    successMessage: string,
    onSuccess: () => void
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestChangeUserGroups());

      try {
        await api.changeUserGroups({
          replace: stateId,
          id: (replaceIds || []).map(replaceId => replaceId.value),
          applyOnDescendants,
          data: requestedSearchModel
        });

        dispatch(new ReceiveChangeUserGroups());
        dispatch(new SuccessNotification(
          replaceTemplateString(
            successMessage,
            numberOfResults)
        ));

        onSuccess();
      } catch (err) {

        dispatch(new FailChangeUserGroups());

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        if (!!err && !!err.validationErrors) {
          dispatch(new UpdateChangeUserGroupsValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  clearChangeUserGroups: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new ClearChangeUserGroups());
    },

  updateChangeUserGroupsIsDirty: (value: boolean): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateChangeUserGroupsIsDirty(value));
  },

  updateChangeUserGroupsState: (value: ChangeOptionType): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateChangeUserGroupsState(value));
  },

  updateChangeUserGroupsFieldsByKey: <T>(key: ChangeOwnerField, value: T): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ChangeUserGroupsFieldsByKey(key, value));
    },

  resetChangeUserGroupsValidation: (key: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetChangeUserGroupsValidation(key));
  },

  changeUsers: (
    stateId: ChangeOptionType,
    replaceIds: ComplexValue<number>[],
    applyOnDescendants: boolean,
    numberOfResults: number,
    requestedSearchModel: FavouriteSearchModel,
    successMessage: string,
    onSuccess: () => void
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestChangeUsers());

      try {
        await api.changeUsers({
          replace: stateId,
          id: (replaceIds || []).map(replaceId => replaceId.value),
          applyOnDescendants,
          data: requestedSearchModel
        });

        dispatch(new ReceiveChangeUsers());
        dispatch(new SuccessNotification(
          replaceTemplateString(
            successMessage,
            numberOfResults)
        ));

        onSuccess();
      } catch (err) {

        dispatch(new FailChangeUsers());

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        if (!!err && !!err.validationErrors) {
          dispatch(new UpdateChangeUsersValidation(complexValidationFromServer(err.validationErrors)));
        }
      }
    },

  clearChangeUsers: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new ClearChangeUsers());
    },

  updateChangeUsersIsDirty: (value: boolean): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateChangeUsersIsDirty(value));
  },

  updateChangeUsersState: (value: ChangeOptionType): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateChangeUsersState(value));
  },

  updateChangeUsersFieldsByKey: <T>(key: ChangeOwnerField, value: T): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ChangeUsersFieldsByKey(key, value));
    },

  resetChangeUsersValidation: (key: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetChangeUsersValidation(key));
  },

  displayRegenerateAggregatedTables: (endpoint: string, description: string) => {
    return new DisplayModal(MODAL.regenerateAggregatedTables, endpoint, description);
  },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    },

  clearList: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      const requestId = Date.now();
      dispatch(new ClearList(requestId));
    }
};
